"use client";

import { TarotRequestCreate, TarotRequestResponse, Language } from "@/types/tarot";
import { PricingConfig } from "@/services/pricing";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { TarotApiService } from "@/services/tarotApi";
import MercadoPagoPaymentBrick from "@/components/payments/MercadoPagoPaymentBrick";
import { config, getApiLanguage } from "@/config/app";
import { useI18n } from "@/i18n";
import { formatCurrencyFromCents } from "@/config/currency";
import { getCampaignSource } from "@/lib/campaignAttribution";
import { getPersistedUtm } from "@/lib/tracking";
import { getFlowId } from "@/services/analytics";
import { saveHotmartReturnContext } from "@/lib/hotmartReturn";
import { toSuccessLoveMap } from "@/lib/loveMap";
import Image from "next/image";
import { CheckoutAddonCard } from "./CheckoutAddonCard";
import { ArrowRightIcon, BoltIcon, ClockIcon, TagIcon } from "./checkoutIcons";
import {
  EMPTY_LOVE_MAP_DRAFT,
  LoveMapDraft,
  LoveMapField,
  LoveMapUpsell,
  resolveLoveMapDraft,
} from "./LoveMapUpsell";

const StripePaymentBrick = lazy(() => import("@/components/payments/StripePaymentBrick"));

interface Step8PaymentProps {
  readonly formData: TarotRequestCreate;
  readonly onPaymentError?: (error: string) => void;
  readonly onMercadoPagoSuccess?: (status: "approved" | "authorized") => void;
  readonly pricingConfig: PricingConfig;
  readonly onRequestIdReady?: (id: number) => void;
  readonly onRequestCreated?: (request: TarotRequestResponse) => void;
}

const DIAL_CODES: Record<string, string> = {
  BR: "55",
  US: "1",
  AR: "54",
  PT: "351",
  ES: "34",
  MX: "52",
  CO: "57",
  CL: "56",
};

/** Phone in the international format stored by the API (+<dial code><digits>). */
function withDialCode(raw: string, country?: string | null): string {
  const onlyDigits = raw.replaceAll(/\D/g, "");
  if (raw.trim().startsWith("+")) return "+" + onlyDigits;
  return `+${DIAL_CODES[country || "BR"] || "55"}${onlyDigits}`;
}

export function Step8Payment({
  formData,
  onPaymentError = (error) => console.error("Erro no pagamento:", error),
  onMercadoPagoSuccess,
  pricingConfig,
  onRequestIdReady,
  onRequestCreated,
}: Step8PaymentProps) {
  const { t } = useI18n();
  const formatBirthDate = (value?: string | null) => {
    const base = (value || "").trim();
    if (!base) return t("value.not_informed", "Não informado");

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(base);
    if (!match) return base;

    const [, year, month, day] = match;
    return config.i18n.language === "en-US"
      ? `${month}/${day}/${year}`
      : `${day}/${month}/${year}`;
  };

  const [mpLoading, setMpLoading] = useState(false);
  const [mpRequestId, setMpRequestId] = useState<number | null>(null);
  const [couponInput, setCouponInput] = useState<string>("");
  const [couponApplied, setCouponApplied] = useState<boolean>(false);
  const [showCouponField, setShowCouponField] = useState<boolean>(false);
  const [priorityDelivery, setPriorityDelivery] = useState(false);
  const [loveMapSelected, setLoveMapSelected] = useState(false);
  const [loveMapDraft, setLoveMapDraft] = useState<LoveMapDraft>(EMPTY_LOVE_MAP_DRAFT);
  const [loveMapErrors, setLoveMapErrors] = useState<ReadonlySet<LoveMapField>>(new Set());
  const [loveMapPurchase, setLoveMapPurchase] = useState<{
    key: string;
    purchased: boolean;
  } | null>(null);
  const [paymentAmountCents, setPaymentAmountCents] = useState<number | null>(null);
  const [previousPayerEmailResult, setPreviousPayerEmailResult] = useState<{
    phone: string;
    email: string | null;
  } | null>(null);
  const lookupPreviousPayerEmail =
    config.payments.provider === "mercadopago" &&
    formData.delivery_option === "whatsapp" &&
    Boolean(formData.querent_phone?.trim());
  const payerEmailLookupLoading = lookupPreviousPayerEmail &&
    previousPayerEmailResult?.phone !== formData.querent_phone;

  useEffect(() => {
    const phone = formData.querent_phone;
    if (!lookupPreviousPayerEmail || !phone) return;

    let cancelled = false;
    TarotApiService.getPreviousPayerEmail(phone)
      .then((email) => {
        if (!cancelled) setPreviousPayerEmailResult({ phone, email });
      })
      .catch(() => {
        if (!cancelled) setPreviousPayerEmailResult({ phone, email: null });
      });
    return () => { cancelled = true; };
  }, [lookupPreviousPayerEmail, formData.querent_phone]);

  const payerEmail = formData.delivery_option === "whatsapp"
    ? (previousPayerEmailResult?.phone === formData.querent_phone
      ? previousPayerEmailResult.email || undefined
      : undefined)
    : formData.querent_email;

  const couponConfigured =
    (config.pricing.coupon?.name || "").trim().length > 0 &&
    (config.pricing.coupon?.percent || 0) > 0;

  const normalizedCouponName = (config.pricing.coupon?.name || "")
    .trim()
    .toLowerCase();

  const couponValid = useMemo(() => {
    if (!couponConfigured) return false;
    return couponInput.trim().toLowerCase() === normalizedCouponName;
  }, [couponConfigured, couponInput, normalizedCouponName]);

  const basePriceCents = pricingConfig.price_cents || 0;
  const discountedPriceCents = useMemo(() => {
    if (!couponApplied || !couponValid) return basePriceCents;
    const pct = Math.max(
      0,
      Math.min(100, Number(config.pricing.coupon?.percent || 0)),
    );
    const discount = Math.round((basePriceCents * pct) / 100);
    return Math.max(0, basePriceCents - discount);
  }, [basePriceCents, couponApplied, couponValid]);

  const discountAmountCents = useMemo(() => {
    if (!(couponApplied && couponValid)) return 0;
    return Math.max(0, basePriceCents - discountedPriceCents);
  }, [couponApplied, couponValid, basePriceCents, discountedPriceCents]);

  const marketingOriginalPriceCents = useMemo(() => {
    const rawOriginalCents = Math.round(basePriceCents / 0.7);
    const whole = Math.floor(rawOriginalCents / 100);
    const lower90 = whole * 100 + 90;
    const upper90 = (whole + 1) * 100 + 90;
    return (
      Math.abs(rawOriginalCents - lower90) <= Math.abs(upper90 - rawOriginalCents)
        ? lower90
        : upper90
    );
  }, [basePriceCents]);

  const payablePriceCents =
    couponApplied && couponValid ? discountedPriceCents : basePriceCents;
  const priorityConfig = pricingConfig.priority_delivery;
  const priorityFeeCents =
    priorityDelivery && priorityConfig?.enabled ? priorityConfig.fee_cents : 0;
  const loveMapConfig = pricingConfig.love_map;
  // The love map is offered once per forecast year: customers who already
  // bought this year's map (same email or phone) do not see it again.
  const loveMapEmail = formData.querent_email?.trim() || "";
  const loveMapPhone = formData.querent_phone?.trim()
    ? withDialCode(formData.querent_phone, formData.querent_phone_country)
    : "";
  const loveMapLookupKey = `${loveMapEmail.toLowerCase()}|${loveMapPhone}`;
  const loveMapNeedsLookup = Boolean(loveMapConfig?.enabled) && loveMapLookupKey !== "|";
  useEffect(() => {
    if (!loveMapNeedsLookup) return;
    let cancelled = false;
    TarotApiService.hasPurchasedLoveMap({
      email: loveMapEmail || undefined,
      phone: loveMapPhone || undefined,
      language: getApiLanguage(),
    })
      .then((purchased) => {
        if (!cancelled) setLoveMapPurchase({ key: loveMapLookupKey, purchased });
      })
      .catch(() => {
        // Fail open: the API refuses a second purchase anyway.
        if (!cancelled) setLoveMapPurchase({ key: loveMapLookupKey, purchased: false });
      });
    return () => {
      cancelled = true;
    };
  }, [loveMapNeedsLookup, loveMapLookupKey, loveMapEmail, loveMapPhone]);
  const loveMapAvailable =
    Boolean(loveMapConfig?.enabled) &&
    (loveMapNeedsLookup
      ? loveMapPurchase?.key === loveMapLookupKey && !loveMapPurchase.purchased
      : !loveMapPurchase?.purchased);
  const loveMapActive = loveMapSelected && loveMapAvailable;
  const loveMapFeeCents = loveMapActive && loveMapConfig ? loveMapConfig.fee_cents : 0;
  const totalPriceCents = payablePriceCents + priorityFeeCents + loveMapFeeCents;
  const showCouponOption = couponConfigured && !mpLoading && !mpRequestId;

  // "De {price} por": the original price is struck through between the two parts.
  const [fromBefore, fromAfter = ""] = t("step8.product.from_to", "De {price} por").split("{price}");

  const formatDeliveryTime = (minutes: number) => {
    if (minutes >= 60 && minutes % 60 === 0) {
      const hours = minutes / 60;
      return (
        hours === 1
          ? t("step8.delivery.hour", "{hours} hora")
          : t("step8.delivery.hours", "{hours} horas")
      ).replace("{hours}", String(hours));
    }
    return t("step8.delivery.minutes", "{minutes} minutos").replace(
      "{minutes}",
      String(minutes),
    );
  };

  const standardDeliveryTime = formatDeliveryTime(
    pricingConfig.standard_delivery_minutes ?? 120,
  );
  const priorityDeliveryTime = priorityConfig
    ? formatDeliveryTime(priorityConfig.delivery_minutes)
    : "";
  const loveMapDeliveryTime = (() => {
    const hours = loveMapConfig?.delivery_hours ?? 0;
    if (hours >= 24 && hours % 24 === 0) {
      const days = hours / 24;
      return (
        days === 1 ? t("step8.delivery.day", "{days} dia") : t("step8.delivery.days", "{days} dias")
      ).replace("{days}", String(days));
    }
    return formatDeliveryTime(hours * 60);
  })();

  const selectedAddons = [
    priorityDelivery && priorityConfig?.enabled
      ? t("step8.total.priority", "entrega prioritária")
      : null,
    loveMapActive && loveMapConfig
      ? t("step8.total.love_map", "Mapa do Amor {year}").replace(
          "{year}",
          String(loveMapConfig.forecast_year),
        )
      : null,
  ].filter((item): item is string => Boolean(item));
  const addonsSummary =
    selectedAddons.length === 0
      ? t("step8.total.no_addons", "Sem adicionais selecionados")
      : t("step8.total.with_addons", "Inclui {items}").replace(
          "{items}",
          selectedAddons.join(t("step8.total.and", " e ")),
        );

  const startPayment = async () => {
    const loveMap = loveMapActive
      ? resolveLoveMapDraft(loveMapDraft, config.i18n.language !== "en-US")
      : null;
    if (loveMap && !loveMap.payload) {
      setLoveMapErrors(loveMap.missing);
      document
        .getElementById("love-map-situation")
        ?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      return;
    }
    setLoveMapErrors(new Set());

    try {
      setMpLoading(true);
      try {
        sessionStorage.removeItem("mp_payer_email");
      } catch {
        // Storage can be unavailable in private browsing.
      }
      await TarotApiService.forceTokenRenewal();

      const deliveryOption = formData.delivery_option || "email";
      const wantsWhatsapp = deliveryOption === "whatsapp" || deliveryOption === "email_whatsapp";
      const wantsEmail = deliveryOption === "email" || deliveryOption === "email_whatsapp";

      const created = await TarotApiService.createTarotRequestAwaitingPayment({
        ...formData,
        campaign_source: getCampaignSource(),
        ...getPersistedUtm(),
        flow_id: getFlowId(),
        language: getApiLanguage() as Language,
        payment_provider: config.payments.provider,
        coupon_code: couponApplied && couponValid ? couponInput.trim() : undefined,
        priority_delivery: priorityDelivery,
        love_map: loveMap?.payload ?? undefined,
        relationship_status: {
          together: false,
          separated: false,
          romantic_interest: true,
        },
        delivery_option: deliveryOption,
        querent_email: wantsEmail ? formData.querent_email : "",
        querent_phone: wantsWhatsapp
          ? withDialCode(formData.querent_phone || "", formData.querent_phone_country)
          : "",
        querent_phone_confirmation: wantsWhatsapp
          ? (() => {
              const raw =
                formData.querent_phone_confirmation || formData.querent_phone || "";
              return raw ? withDialCode(raw, formData.querent_phone_country) : raw;
            })()
          : "",
      });

      if (onRequestIdReady && typeof created.id === "number") {
        onRequestIdReady(created.id);
      }
      onRequestCreated?.(created);

      if (config.payments.provider === "hotmart") {
        const checkout = await TarotApiService.createHotmartCheckout(created.id);
        saveHotmartReturnContext({
          requestId: created.id,
          checkoutToken: checkout.checkout_token,
          querentName: formData.querent.full_name || "Cliente",
          email: wantsEmail ? formData.querent_email || undefined : undefined,
          deliveryOption,
          whatsappNumber:
            formData.delivery_option !== "email"
              ? formData.querent_phone || undefined
              : undefined,
          amountCents: created.hotmart_amount_cents ?? totalPriceCents,
          currency: created.hotmart_currency || pricingConfig.currency,
          priorityDelivery: created.is_priority_delivery ?? priorityDelivery,
          priorityDeliveryFeeCents:
            created.priority_delivery_fee_cents ?? priorityFeeCents,
          deliveryTargetMinutes:
            created.delivery_target_minutes ??
            (priorityDelivery
              ? priorityConfig?.delivery_minutes
              : pricingConfig.standard_delivery_minutes),
          loveMap: toSuccessLoveMap(created.love_map),
          createdAt: Date.now(),
        });
        window.location.assign(checkout.checkout_url);
        return;
      }

      setMpRequestId(created.id as unknown as number);
      setPaymentAmountCents(
        created.stripe_amount_cents ?? created.mp_amount_cents ?? totalPriceCents,
      );
    } catch (e) {
      if ((e as Error)?.message === "love_map_already_purchased") {
        setLoveMapSelected(false);
        setLoveMapPurchase({ key: loveMapLookupKey, purchased: true });
        onPaymentError?.(
          t(
            "step8.love_map.already_purchased",
            "Você já garantiu o seu Mapa do Amor {year} em um pedido anterior, então removemos essa opção. Continue para finalizar a sua carta.",
          ).replace("{year}", String(loveMapConfig?.forecast_year ?? "")),
        );
        return;
      }
      const msg =
        (e as Error)?.message || "Falha ao iniciar pagamento Mercado Pago";
      onPaymentError?.(msg);
    } finally {
      setMpLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      {config.ui.showPaymentRequestSummary && (
        <div className="bg-white/40 p-6 rounded-lg border border-[rgba(232,115,115,0.2)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-tarot-marrom-escuro mb-2 font-title">
                {t("summary.querent", "👤 Informações do Consulente")}
              </h4>
              <ul className="form-description-text space-y-2">
                <li>
                  <span className="font-title">{t("summary.name", "Nome")}:</span>{" "}
                  {formData.querent.full_name}
                </li>
                {formData.querent_phone?.trim() && (
                  <li>
                    <span className="font-title">
                      {t("summary.whatsapp", "WhatsApp")}:
                    </span>{" "}
                    {formData.querent_phone}
                  </li>
                )}
                {formData.delivery_option !== "whatsapp" && formData.querent_email?.trim() && (
                  <li>
                    <span className="font-title">
                      {t("summary.email", "Email")}:
                    </span>{" "}
                    {formData.querent_email}
                  </li>
                )}
                <li>
                  <span className="font-title">
                    {t("summary.birth_date", "Data de nascimento")}:
                  </span>{" "}
                  {formatBirthDate(
                    (
                      formData.querent as unknown as {
                        birth_date?: string | null;
                      }
                    ).birth_date,
                  )}
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-tarot-marrom-escuro mb-2 font-title">
                {t("summary.channeled", "💫 Pessoa Canalizada")}
              </h4>
              <ul className="form-description-text space-y-2">
                <li>
                  <span className="font-title">{t("summary.name", "Nome")}:</span>{" "}
                  {formData.channeled_person.full_name}
                </li>
              </ul>
            </div>
          </div>

          {formData.desired_answers && (
            <div className="mt-6 pt-4 border-t border-[rgba(232,115,115,0.15)]">
              <h4 className="text-tarot-marrom-escuro mb-2 font-title">
                {t("step8.happening.title", "💌 O que está acontecendo")}
              </h4>
              <p className="form-description-text bg-white p-4 rounded-lg border border-[rgba(232,115,115,0.15)]">
                {formData.desired_answers}
              </p>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2.5 sm:space-y-3">
        <section
          aria-label={t("step8.product.name", "Carta Canalizada")}
          className="rounded-2xl border border-[#eadbd2] bg-white p-3.5 shadow-[0_2px_12px_rgba(92,22,22,0.06)] sm:p-5"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-4">
              <Image
                src="/images/produto-final.jpg"
                alt=""
                width={651}
                height={488}
                className="h-16 w-16 shrink-0 rounded-xl object-cover sm:h-24 sm:w-24"
              />
              <div className="min-w-0">
                <h3 className="font-title text-lg leading-tight text-[#5c1616] sm:text-2xl">
                  {t("step8.product.name", "Carta Canalizada")}
                </h3>
                <p className="mt-0.5 text-[13px] leading-snug text-[#8a6a5f] sm:text-sm">
                  {t("step8.product.desc", "Leitura personalizada de tarot")} •{" "}
                  {t("step8.product.format", "PDF digital")}
                </p>
                <span className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-[#f8efea] px-2.5 py-0.5 text-[11px] text-[#4a3230] sm:mt-2 sm:px-3 sm:py-1 sm:text-xs">
                  <ClockIcon className="h-3.5 w-3.5 shrink-0 text-[#a3742c]" />
                  {t("step8.product.delivery", "Entrega em até {time}").replace(
                    "{time}",
                    priorityDelivery && priorityConfig?.enabled
                      ? priorityDeliveryTime
                      : standardDeliveryTime,
                  )}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-[#f1e6df] pt-2.5 sm:block sm:border-l sm:border-t-0 sm:pl-6 sm:pt-0 sm:text-right">
              <p className="text-xs text-[#8a6a5f] sm:text-sm">
                {fromBefore}
                <span className="line-through">
                  {formatCurrencyFromCents(marketingOriginalPriceCents)}
                </span>
                {fromAfter}
              </p>
              <div>
                {couponApplied && couponValid && (
                  <p className="text-xs text-emerald-700">
                    - {formatCurrencyFromCents(discountAmountCents)}
                  </p>
                )}
                <p className="text-right font-title text-2xl leading-tight text-emerald-700 sm:text-3xl">
                  {formatCurrencyFromCents(payablePriceCents)}
                </p>
              </div>
            </div>
          </div>
        </section>

        {priorityConfig?.enabled && !mpRequestId && (
          <CheckoutAddonCard
            tone="gold"
            checked={priorityDelivery}
            onChange={setPriorityDelivery}
            disabled={mpLoading}
            icon={<BoltIcon className="h-4 w-4 text-[#d5a253] sm:h-5 sm:w-5" />}
            title={t("step8.priority.title", "Entrega prioritária")}
            optionalLabel={t("step8.addon.optional", "Opcional")}
            price={formatCurrencyFromCents(priorityConfig.fee_cents)}
          >
            <span className="mt-1 block text-[13px] leading-snug text-[#4a3230] sm:text-sm">
              {t(
                "step8.priority.description",
                "Antecipe sua entrega de até {standard} para até {priority}.",
              )
                .replace("{standard}", standardDeliveryTime)
                .replace("{priority}", priorityDeliveryTime)}{" "}
              <span className="text-[#8a6a5f]">
                {t(
                  "step8.priority.after_payment",
                  "Prazo contado após a confirmação do pagamento.",
                )}
              </span>
            </span>
          </CheckoutAddonCard>
        )}

        {loveMapConfig && loveMapAvailable && !mpRequestId && (
          <LoveMapUpsell
            feeCents={loveMapConfig.fee_cents}
            forecastYear={loveMapConfig.forecast_year}
            deliveryTime={loveMapDeliveryTime}
            channeledName={formData.channeled_person.full_name}
            selected={loveMapSelected}
            onSelectedChange={(selected) => {
              setLoveMapSelected(selected);
              setLoveMapErrors(new Set());
            }}
            draft={loveMapDraft}
            onDraftChange={(draft) => {
              setLoveMapDraft(draft);
              setLoveMapErrors(new Set());
            }}
            errors={loveMapErrors}
            disabled={mpLoading}
          />
        )}

        {showCouponOption && (
          <div className="mb-2">
            {!showCouponField ? (
              <button
                type="button"
                onClick={() => setShowCouponField(true)}
                className="inline-flex items-center gap-2 px-1 text-sm text-[#4a3230] underline underline-offset-2 hover:text-[#5c1616]"
              >
                <TagIcon className="h-5 w-5 text-[#a3742c]" />
                {t("step8.coupon.link", "Tenho um cupom de Desconto")}
              </button>
            ) : (
              <div className="bg-white rounded-lg p-3 border border-[rgba(232,115,115,0.2)]">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <input
                    type="text"
                    id="coupon-input"
                    value={couponInput}
                    onChange={(e) => {
                      setCouponInput(e.target.value);
                      setCouponApplied(false);
                    }}
                    placeholder={t("step8.coupon.placeholder", "Insira seu cupom")}
                    className="flex-1 border rounded px-3 py-2 text-sm disabled:bg-gray-100 disabled:text-gray-500 disabled:border-gray-300 disabled:cursor-not-allowed"
                    disabled={couponApplied && couponValid}
                  />
                  {couponApplied ? (
                    <button
                      type="button"
                      onClick={() => {
                        setCouponApplied(false);
                        setCouponInput("");
                      }}
                      className="px-4 py-2 rounded bg-gray-200 text-gray-800 text-sm hover:bg-gray-300"
                    >
                      {t("step8.coupon.remove", "Remover")}
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setCouponApplied(true)}
                      className="w-full sm:w-auto px-4 py-2 rounded bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-60"
                      disabled={couponInput.trim().length === 0}
                      title={
                        couponInput.trim().length === 0
                          ? t("step8.coupon.enter_coupon_title", "Digite um cupom")
                          : t("step8.coupon.apply_title", "Aplicar")
                      }
                    >
                      {t("step8.coupon.apply", "Aplicar")}
                    </button>
                  )}
                </div>
                {couponApplied && !couponValid && (
                  <p className="text-sm text-red-600 mt-1">
                    {t("step8.coupon.invalid", "Cupom inválido.")}
                  </p>
                )}
                {couponApplied && couponValid && (
                  <p className="text-sm text-emerald-700 mt-1">
                    {t("step8.coupon.applied_prefix", "Cupom aplicado:")}{" "}
                    {config.pricing.coupon?.percent}% OFF
                  </p>
                )}
              </div>
            )}
          </div>
        )}


        <section className="flex items-center justify-between gap-4 rounded-2xl border border-[#eadbd2] bg-white px-3.5 py-2.5 sm:px-5 sm:py-3">
          <div>
            <p className="font-title text-lg leading-tight text-[#5c1616] sm:text-xl">{t("step8.total.title", "Total")}</p>
            <p className="text-xs text-[#8a6a5f]">{addonsSummary}</p>
          </div>
          <p className="shrink-0 border-l border-[#f1e6df] pl-4 font-title text-2xl text-emerald-700 sm:text-3xl">
            {formatCurrencyFromCents(
              mpRequestId ? (paymentAmountCents ?? totalPriceCents) : totalPriceCents,
            )}
          </p>
        </section>

        <div
          className={
            mpRequestId
              ? "space-y-4 rounded-2xl border border-[#eadbd2] bg-white p-4 sm:p-5"
              : "space-y-4"
          }
        >
          {mpRequestId ? (
            <div className="pt-2 relative min-h-[400px]">
              {mpLoading && (
                <div
                  className="absolute inset-0 bg-gradient-to-br from-tarot-bege/95 to-white/95 backdrop-blur-sm rounded-lg z-10 flex flex-col items-center justify-center"
                  style={{ boxShadow: "inset 0 0 40px rgba(212, 175, 55, 0.2)" }}
                >
                  <div className="flex flex-col items-center gap-4 p-8">
                    <div className="relative w-20 h-20">
                      <div
                        className="absolute inset-0 rounded-full animate-spin"
                        style={{
                          background:
                            "linear-gradient(135deg, #d4af37, #f4e4b6, #d4af37)",
                          animation: "spin 1.5s linear infinite",
                        }}
                      />
                      <div className="absolute inset-2 rounded-full bg-gradient-to-br from-tarot-bege to-white" />
                      <div className="absolute inset-0 flex items-center justify-center text-3xl">
                        💳
                      </div>
                    </div>
                    <div className="text-center space-y-2">
                      <p className="text-lg font-title text-tarot-marrom-escuro animate-pulse">
                        {config.payments.provider === "stripe"
                          ? t("step8.loading.connecting_stripe", "Conectando com Stripe...")
                          : config.payments.provider === "hotmart"
                            ? t("step8.loading.connecting_hotmart", "Conectando com Hotmart...")
                            : t("step8.loading.connecting", "Conectando com Mercado Pago...")}
                      </p>
                      <p className="text-sm font-subtitle text-tarot-marrom/80">
                        {t(
                          "step8.loading.wait",
                          "Aguarde enquanto preparamos seu pagamento seguro...",
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {config.payments.provider === "mercadopago" && formData.delivery_option === "whatsapp" && !payerEmail && (
                <p className="mb-3 text-sm text-tarot-marrom">
                  {t("step8.payer_email_note", "O Mercado Pago solicitará o email do pagador para processar o pagamento. A carta será entregue apenas pelo WhatsApp.")}
                </p>
              )}
              {config.payments.provider === "stripe" ? (
                <Suspense fallback={<p className="text-center text-sm text-tarot-marrom">{t("step8.loading.connecting_stripe", "Conectando com Stripe...")}</p>}>
                  <StripePaymentBrick
                    requestId={mpRequestId}
                    onSuccess={onMercadoPagoSuccess}
                    payerEmail={payerEmail}
                  />
                </Suspense>
              ) : (
                <MercadoPagoPaymentBrick
                  requestId={mpRequestId}
                  onSuccess={onMercadoPagoSuccess}
                  amountOverrideCents={paymentAmountCents ?? undefined}
                  payerEmail={payerEmail}
                />
              )}
            </div>
          ) : (
            <div className="pt-1 text-center">
              <button
                onClick={startPayment}
                disabled={mpLoading || payerEmailLookupLoading}
                className="flex w-full items-center justify-center gap-2 rounded-xl px-6 py-3.5 font-title text-lg font-bold text-white shadow-lg disabled:opacity-60 sm:py-4"
                style={{
                  background: "linear-gradient(135deg, #10b981, #047857)",
                  boxShadow: "0 8px 20px rgba(4, 120, 87, 0.25)",
                }}
              >
                {(mpLoading || payerEmailLookupLoading) && (
                  <span className="inline-block animate-spin">⏳</span>
                )}
                {mpLoading || payerEmailLookupLoading
                  ? t("step8.pay.start_loading", "Iniciando…")
                  : t("step8.pay.start", "Continuar para pagamento")}
                {!(mpLoading || payerEmailLookupLoading) && <ArrowRightIcon className="h-5 w-5" />}
              </button>
              <p className="mt-3 text-sm text-[#8a6a5f]">
                {t("step8.pay.methods", "🔒 Pagamento seguro via Pix ou Cartão")}
              </p>
              <p className="mt-1 text-sm text-[#8a6a5f]">
                {priorityDelivery && priorityConfig
                  ? t(
                      "step8.priority.badge",
                      "Entrega prioritária em até {time}",
                    ).replace("{time}", priorityDeliveryTime)
                  : t("step8.secure.badge", "⏱ Entrega em até {time}").replace(
                      "{time}",
                      standardDeliveryTime,
                    )}
              </p>
              {config.payments.provider === "stripe" &&
                config.i18n.language === "en-US" && (
                <div className="mt-3 flex justify-center">
                  <Image
                    src="/images/stripe-logo.webp"
                    alt="Stripe"
                    width={96}
                    height={40}
                    className="h-8 w-auto"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
